/* eslint-disable @typescript-eslint/no-explicit-any */
import { toUser } from '@data/dto/user.dto';
import { USER_STORAGE, UserHttp } from '@data/models/user.interface';
import { useStorage } from '@plugins/storage';
import { ActionStore, RootState } from '@stores/models/store';
import dashboardDatatableReducer from '@stores/reducers/dashboard-datatable.reducer';
import appModalReducer from '@stores/reducers/modal.reducer';
import navigationReducer from '@stores/reducers/navigation.reducer';
import appSystemReducer from '@stores/reducers/system.reducer';
import timesheetNavigationReducer from '@stores/reducers/timesheet-navigation.reducer';
import toastReducer from '@stores/reducers/toast.reducer';
import appUserReducer from '@stores/reducers/user.reducer';
import {
  AnyAction,
  Middleware,
  PreloadedState,
  Store,
  applyMiddleware,
  combineReducers,
  createStore,
} from 'redux';
import thunk from 'redux-thunk';

// Define rootReducer first so we can use its type
const rootReducer = combineReducers({
  system: appSystemReducer,
  modal: appModalReducer,
  user: appUserReducer,
  toast: toastReducer,
  dashboardDatatable: dashboardDatatableReducer,
  navigation: navigationReducer,
  timesheetNavigation: timesheetNavigationReducer,
});

// Infer state type from the reducer to ensure type consistency
type AppState = ReturnType<typeof rootReducer>;

const storage = useStorage();
const user = toUser(storage.get<UserHttp>(USER_STORAGE) || {});

export const defaultStore: AppState = {
  user,
  system: {
    title: 'system.title',
    loading: true,
    language: 'fr',
    platform: '',
    os: '',
    anchors: {},
  },
  toast: [],
  modal: {
    show: false,
    title: '',
    component: null,
  },
  dashboardDatatable: {
    limit: 50,
    skip: 0,
  },
  navigation: {
    active: '',
    icon: '',
    menu: [],
    showPageTitle: true,
  },
  timesheetNavigation: {
    list: [],
    currentIndex: 0,
  },
};

const actionSanitizer = (action: ActionStore<object>) => {
  return action.type === 'UPDATE_ANCHORS_REFS'
    ? {
        ...action,
        payload: { ...action.payload, element: '<<REF>>' },
      }
    : action;
};

const stateSanitizer = (state: RootState) => {
  state.system.anchors = {};

  return state;
};

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: any;
  }
}

const middleware: Middleware = thunk;

const devToolsOptions = {
  actionSanitizer,
  stateSanitizer,
  actionsDenylist: 'UPDATE_ANCHORS_REFS',
};

// Cast preloadedState to avoid Redux's deep type instantiation issue
// The type is still enforced via AppState type annotation on defaultStore
const preloadedState = defaultStore as PreloadedState<AppState>;

// Create store with or without Redux DevTools
export const store: Store<AppState, AnyAction> =
  typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__
    ? createStore(
        rootReducer,
        preloadedState,
        window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(devToolsOptions)(
          applyMiddleware(middleware),
        ),
      )
    : createStore(rootReducer, preloadedState, applyMiddleware(middleware));
